import type React from 'react';
import {
  columnAlignClass,
  flexRender,
  type Row,
  type RowData,
} from '@/lib/tanstack-table';
import { TableCell, TableRow, type TableDensity } from '@/components/ui/table';
import { cn } from '@/lib/utils';

/**
 * Stretches an opt-in row link over its whole row. Module-scoped so the class
 * string never appears as a literal inside JSX (the i18n linter reads those
 * as user-facing copy).
 */
const ROW_LINK_CLASS = 'absolute inset-0 z-[1]';

/** Selection checkboxes use this column id across list tables. */
const SELECTION_COLUMN_ID = 'select';

/** True when the point is inside the rect of a cell that has been laid out. */
function isInsideCell(cell: HTMLElement, x: number, y: number): boolean {
  const rect = cell.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

/**
 * True when the click landed in the selection cell, including the padding
 * around the checkbox. The checkbox itself stops propagation, so this only
 * sees near-misses that would otherwise open the row.
 */
function isSelectionColumnClick(event: React.MouseEvent): boolean {
  if (!(event.target instanceof Element)) return false;
  // The stretched row link lives in the first cell but covers the whole row,
  // so a hit on that link is not itself "the selection column".
  if (event.target.closest('a')) {
    const cell = event.target
      .closest('tr')
      ?.querySelector<HTMLElement>(`[data-column-id="${SELECTION_COLUMN_ID}"]`);
    return cell ? isInsideCell(cell, event.clientX, event.clientY) : false;
  }
  const cell = event.target.closest('[data-column-id]');
  return cell?.getAttribute('data-column-id') === SELECTION_COLUMN_ID;
}

/** Attributes a virtualized body sets on each drawn row to measure it. */
export interface RowMeasure {
  'data-index': number | undefined;
  ref: (el: Element | null) => void;
}

/** A collapsible group's header line, spanning every column. */
export function DataTableGroupRow({
  measure,
  density,
  colSpan,
  children,
}: {
  measure?: RowMeasure;
  density?: TableDensity;
  colSpan: number;
  children: React.ReactNode;
}) {
  const compact = density === 'compact';
  return (
    <TableRow
      {...measure}
      className={cn(
        'bg-muted hover:bg-muted border-t-border',
        compact ? 'border-t' : 'border-t-2',
      )}
    >
      <TableCell colSpan={colSpan} className={compact ? 'py-0' : 'py-2'}>
        {children}
      </TableCell>
    </TableRow>
  );
}

interface BodyRowProps<TData extends RowData> {
  row: Row<TData>;
  measure?: RowMeasure;
  domId?: string;
  selected: boolean;
  ariaSelected?: boolean;
  className?: string;
  /** Fixed table layout: cells clip, and a width-less filler cell follows. */
  fixedLayout: boolean;
  onRowClick?: (
    row: TData,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => void;
  onRowMouseDown?: (
    row: TData,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => void;
  onRowIntent?: (row: TData) => void;
  onRowContextMenu?: (e: React.MouseEvent, row: TData) => void;
  renderRowLink?: (row: TData, className: string) => React.ReactNode;
}

// Only the stretched row link needs cancelling. The checkbox handles its own
// click, and preventDefault there stops it from toggling.
function cancelLinkInSelectionCell<TData extends RowData>(
  row: Row<TData>,
  event: React.MouseEvent,
) {
  const onLink =
    event.target instanceof Element && event.target.closest('a') !== null;
  if (onLink && row.getCanSelect() && isSelectionColumnClick(event)) {
    event.preventDefault();
  }
}

/** One data row: selection-cell clicks toggle it, other clicks open it. */
export function DataTableBodyRow<TData extends RowData>({
  row,
  measure,
  domId,
  selected,
  ariaSelected,
  className,
  fixedLayout,
  onRowClick,
  onRowMouseDown,
  onRowIntent,
  onRowContextMenu,
  renderRowLink,
}: BodyRowProps<TData>) {
  // Cells read live table state (selection, sorting) through the stable `row`
  // object, which the compiler cannot see change. Re-render them every time,
  // as the inline rows did.
  'use no memo';
  const intent = onRowIntent && (() => onRowIntent(row.original));
  return (
    <TableRow
      {...measure}
      // Stable DOM handle for the row (TanStack row id, i.e. the owner's
      // getRowId) so callers can scroll/focus a specific row after a remount
      // — e.g. restoring list context when a detail pane closes.
      data-row-id={row.id}
      id={domId}
      data-state={selected ? 'selected' : undefined}
      aria-selected={ariaSelected}
      onClickCapture={(event) => cancelLinkInSelectionCell(row, event)}
      onMouseDown={(event) => onRowMouseDown?.(row.original, event)}
      onClick={(event) => {
        if (row.getCanSelect() && isSelectionColumnClick(event)) {
          row.toggleSelected();
          return;
        }
        onRowClick?.(row.original, event);
      }}
      onMouseEnter={intent}
      onFocus={intent}
      onContextMenu={
        onRowContextMenu &&
        ((e) => {
          e.preventDefault();
          onRowContextMenu(e, row.original);
        })
      }
      className={cn(
        (onRowClick || renderRowLink) && 'cursor-pointer',
        // The stretched link positions against the row. Note this must not
        // promote descendant anchors, or it would override the link's own
        // `absolute`.
        renderRowLink && 'relative',
        className,
      )}
    >
      {row.getVisibleCells().map((cell, cellIndex) => {
        const hostsLink = cellIndex === 0 && renderRowLink !== undefined;
        return (
          <TableCell
            key={cell.id}
            data-column-id={cell.column.id}
            // The row link is an overlay anchored to the row, so clipping its
            // host cell would crop it to that one column. Every other cell
            // still clips.
            className={cn(
              fixedLayout && !hostsLink && 'overflow-hidden',
              columnAlignClass(cell.column.columnDef.meta),
            )}
          >
            {hostsLink && renderRowLink(row.original, ROW_LINK_CLASS)}
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
      {fixedLayout && <TableCell aria-hidden className="p-0" />}
    </TableRow>
  );
}
